import React from 'react';

interface SparklesCoreProps {
  className?: string;
  background?: string;
  minSize?: number;
  maxSize?: number;
  particleDensity?: number;
  particleColor?: string;
}

const SparklesCore: React.FC<SparklesCoreProps> = ({
  className,
  background = 'transparent',
  minSize = 0.5,
  maxSize = 1.2,
  particleDensity = 800,
  particleColor = '#FFFFFF',
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const animationRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = canvas.clientWidth;
    let height = canvas.clientHeight;

    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    const particles: {
      x: number;
      y: number;
      r: number;
      vx: number;
      vy: number;
      alpha: number;
      life: number;
      maxLife: number;
    }[] = [];

    const count = Math.max(40, Math.min(160, Math.floor(particleDensity / 40)));
    const random = (min: number, max: number) => Math.random() * (max - min) + min;

    for (let i = 0; i < count; i += 1) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        r: random(minSize, maxSize),
        vx: random(-0.2, 0.2),
        vy: random(0.1, 0.4),
        alpha: random(0.3, 0.9),
        life: 0,
        maxLife: random(4, 10),
      });
    }

    const loop = () => {
      animationRef.current = requestAnimationFrame(loop);
      ctx.clearRect(0, 0, width, height);

      if (background !== 'transparent') {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, width, height);
      }

      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life += 0.016;

        if (particle.y > height + 10 || particle.life > particle.maxLife) {
          particle.x = Math.random() * width;
          particle.y = -10;
          particle.life = 0;
          particle.maxLife = random(4, 10);
          particle.vx = random(-0.2, 0.2);
          particle.vy = random(0.1, 0.4);
          particle.r = random(minSize, maxSize);
          particle.alpha = random(0.3, 0.9);
        }

        const lifeFactor = 1 - particle.life / particle.maxLife;
        const alpha = Math.max(0, Math.min(1, particle.alpha * lifeFactor));

        ctx.beginPath();
        ctx.fillStyle = particleColor;
        ctx.globalAlpha = alpha;
        ctx.arc(particle.x, particle.y, particle.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      });
    };

    loop();

    return () => {
      if (animationRef.current != null) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', resize);
    };
  }, [background, maxSize, minSize, particleColor, particleDensity]);

  return <canvas ref={canvasRef} className={className} style={{ display: 'block' }} />;
};

function getMaskedName(name: string): { visiblePrefix: string; maskedTail: string } {
  const full = String(name || '').trim();
  if (!full) return { visiblePrefix: 'Ar', maskedTail: 'XXXXXXXX' };
  const visiblePrefix = full.slice(0, 2);
  const maskedTail = full
    .slice(2)
    .split('')
    .map((char) => (char === ' ' ? ' ' : 'X'))
    .join('');
  return { visiblePrefix, maskedTail };
}

export const SparkleMaskedName: React.FC<{ name: string; className?: string }> = ({
  name,
  className,
}) => {
  const { visiblePrefix, maskedTail } = getMaskedName(name);

  return (
    <span className={`relative inline select-none ${className ?? ''}`.trim()}>
      <span className="sr-only">{name}</span>
      <span aria-hidden="true" className="inline">
        <span>{visiblePrefix}</span>
        {maskedTail && (
          <span className="relative inline align-baseline">
            <span className="invisible select-none">{maskedTail}</span>
            <span className="pointer-events-none absolute inset-0">
              <SparklesCore
                background="transparent"
                minSize={1}
                maxSize={1}
                particleDensity={2000000000000000000000}
                className="h-full w-full py-0.5"
                particleColor="#FFFFFF"
              />
            </span>
          </span>
        )}
      </span>
    </span>
  );
};
